import { LocalStorageService } from './local-storage.service';
import { AuthenticationService } from './authentication.service';
import { Injectable } from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import { ApiClientHelper } from '../helpers/api-client.helper';
import { AppConfig } from '../../shared/config/app-config';
import { UserAlias } from '../../shared/constants';
import { StringHelper } from '../helpers/strings.helper';
import {
	AgentDashBoardModel,
	AggregatorDashBoardModel,
	OutletManagerDashBoardModel,
	UnlinkOutlets,
} from '../../shared/models/dashboard.model';
import { Survey } from '../../shared/models/utility.model';
import { BusinessExpansionService } from './business-expansion.service';
import { UtilityService } from './utility.service';
import { StorageKeys } from '../../shared/models/storage-keys';

@Injectable({
	providedIn: 'root',
})
export class DashboardService {
	dashboardData: any = new ReplaySubject(1);
	mobileCompulsoryModal = new ReplaySubject<string>(1);
	public storedAgentDashboardData$ = new Subject();
	private agentHasSurvey = false;
	private surveyQuestions: Survey[] = [];
	private monitorGrafanaUrl: string;
	private storedOutletManagerDashBoardData: OutletManagerDashBoardModel;
	private coordinatorState: string;
	private livenessCheckComplete = false;
	private unlinkedOutlets: UnlinkOutlets[] = [];
	private agentSurveyEnabled = false;
	private canDoLivenessCheck = false;
	private retryRmDetailsCall = false;

	constructor(
		private apiClientHelper: ApiClientHelper,
		public config: AppConfig,
		public auth: AuthenticationService,
		private localStorage: LocalStorageService,
		private utilityService: UtilityService,
		private businessExpansionService: BusinessExpansionService
	) { }

	getContextUser() {
		const role = this.localStorage.get('selected_role');
		switch (role) {
			case 'MONNIFY_AGENCY_BANKING_AGENT':
			case 'MONNIFY_AGENCY_BANKING_GENERIC_STAFF_USER':
				return 'agents';
			case UserAlias.aggregator:
				return 'aggregators';
			case UserAlias.areaManager:
				return 'area-manager';
			case UserAlias.superAggregator:
				return 'super-aggregators';
			case UserAlias.supremeAggregator:
				return 'supreme-aggregator';
			case 'OUTLET_MANAGER':
			case 'MONNIFY_AGENT_MANAGER':
				return 'agent-managers';
		}
	}

	getSurveyQuestions() {
		return this.surveyQuestions;
	}

	getUnlinkedOutlets() {
		return this.unlinkedOutlets;
	}

	getCanDolivenessCheck() {
		return this.canDoLivenessCheck;
	}

	setAgentSurveyEnabled(enabled) {
		this.agentSurveyEnabled = enabled;
	}

	getAgentSurveyEnabled() {
		return this.agentSurveyEnabled;
	}

	setUnlinkedOutlets(outlets: UnlinkOutlets[]) {
		this.unlinkedOutlets = outlets;
	}

	getStoredAgentDashboardData() {
		const savedData = this.localStorage.get('agentDashboardData');
		if (!savedData) {
			return null;
		}
		return JSON.parse(savedData);
	}

	getBookkeepingStatus() {
		const dashboardData = this.getStoredAgentDashboardData();
		const bookkeepingStatus = dashboardData?.bookkeepingBusinessStatus;
		if (bookkeepingStatus === 'PENDING' || StringHelper.isNullOrEmptyOrUndefined(dashboardData)) {
			return this.getBusinessProfile()
				.then((res) => {
					const businessStatus = res?.data?.businessStatus;
					this.setStoredAgentDashboardData({
						...dashboardData,
						bookkeepingBusinessStatus: businessStatus,
					});
					return businessStatus;
				})
				.catch((res) => {
					return bookkeepingStatus;
				});
		} else {
			return Promise.resolve(bookkeepingStatus);
		}
	}

	getStoredOutletManagerDashBoardData() {
		return this.storedOutletManagerDashBoardData;
	}

	setStoredOutletManagerDashBoardData(outletManagerDashBoardData: OutletManagerDashBoardModel) {
		this.storedOutletManagerDashBoardData = outletManagerDashBoardData;
	}

	setLivenessCheckComplete(livenessCheck) {
		this.livenessCheckComplete = livenessCheck;
	}

	getLivenessCheckComplete() {
		return this.livenessCheckComplete;
	}

	setStoredAgentDashboardData(agentDashboardData: AgentDashBoardModel) {
		if (!agentDashboardData) {
			this.localStorage.deleteValue('agentDashboardData');
		}
		this.localStorage.save('agentDashboardData', JSON.stringify(agentDashboardData));
		this.storedAgentDashboardData$.next(agentDashboardData);
	}

	getCoordinatorState() {
		return this.coordinatorState;
	}

	setCoordinatorState(coordinatorState: string) {
		this.coordinatorState = coordinatorState;
	}

	getMonitorGrafanaUrl() {
		return this.monitorGrafanaUrl;
	}

	setMonitorGrafanaUrl(monitorGrafanaUrl: string) {
		this.monitorGrafanaUrl = monitorGrafanaUrl;
	}

	setSurveyQuestions(survey: Survey[]) {
		this.surveyQuestions = survey;
	}

	setAgentHasSurvey(hasSurvey) {
		this.agentHasSurvey = hasSurvey;
	}

	getAgentHasSurvey() {
		return this.agentHasSurvey;
	}

	loadDashboardData(user: string): void {
		switch (user) {
			case UserAlias.aggregator:
				this.loadAggregatorDashboardData();
				break;
		}
	}

	loadOutletDashboardData() {
		this.getOutletManagerDashboardData()
			.then((data) => {
				this.livenessCheckComplete = data.livenessCheckComplete;
				this.dashboardData.next(data);
			})
			.catch((error) => {
				const message = StringHelper.getErrorMessageHttpErrorResponse(error);
				this.dashboardData.error(message);
			});
	}

	loadAggregatorDashboardData() {
		this.getAggregatorDashboardData()
			.then((data:any) => {
				this.dashboardData.next(data);
				this.auth.setFeatureAccess(data.featureAccess, true);
				this.localStorage.save(StorageKeys.HAS_USER_PIN, data.hasUserPin);
				this.localStorage.save(StorageKeys.IS_BANK_BRM, data?.branchBrm ? data?.branchBrm : false);

				/* if (JSON.parse(this.localStorage.get(StorageKeys.terms_and_conditions_status)) == false) {
					this.utilityService.launchTCModal();
				} */
			})
			.catch((error) => {
				const message = StringHelper.getErrorMessageHttpErrorResponse(error);
				this.dashboardData.error(message);
			});
	}

	getAgentDashboardData(): Promise<AgentDashBoardModel> {
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v1/dashboard/agents?trend=3`;
		return this.apiClientHelper.getForPromise(url);
	}

	getOutletManagerDashboardData(): Promise<OutletManagerDashBoardModel> {
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v1/dashboard/agent-managers?trend=3`;
		return this.apiClientHelper.getForPromise(url);
	}

	getAggregatorDashboardData(): Promise<AggregatorDashBoardModel> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/dashboard/aggregators?trend=3`;
		return this.apiClientHelper.getForPromise(url);
	}

	getAgentSurvey(): Promise<Survey[]> {
		const url = this.config.MONIEPOINT_HOST + `/api/v1/agent-survey/all/open`;
		return this.apiClientHelper.getForPromise(url);
	}

	getUnlinkedOutlet(): Promise<UnlinkOutlets[]> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/agent-managers/unlinked-outlets`;
		return this.apiClientHelper.getForPromise(url);
	}

	getTransactionTrends(request): Promise<GraphData> {
		const params = StringHelper.toUrlString(request);
		const role = this.getContextUser();
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v1/transaction-history/${role}?${params}`;
		return this.apiClientHelper.getForPromise(url);
	}

	getBusinessProfile() {
		const url = `${this.config.MONIEPOINT_ACCOUNTING}/api/v1/businesses/profile`;
		return this.apiClientHelper.getForPromise(url, false, false, true);
	}

	importOutlets(): Promise<any> {
		const url = `${this.config.MONIEPOINT_HOST}/api/v1/agent-managers/unlinked-outlets/link`;
		return this.apiClientHelper.postForPromise(url, {});
	}

	uploadFile(params): Promise<any> {
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v1/files`;
		return this.apiClientHelper.newPutAsFormDataForPromise(url, params);
	}

	getFileInfo(reference: string): Promise<any> {
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v1/files/${reference}`;
		return this.apiClientHelper.getForPromise(url);
	}

  getBusinessCategoriesForBrm(agentId: number): Promise<any> {
		const url = `${this.config.MONIEPOINT_DASHBOARD}/api/v1/agents/${agentId}/business-types`;
		return this.apiClientHelper.getForPromise(url);
	}

	getPendingConsentRequests(status = CONSENT_STATUS.PENDING): Promise<GetConsentResponse> {
		const url = `${this.config.MONIEPOINT_CONSENT_MANAGEMENT_URL}/api/v1/consent-requests?status=${status}`;
		return this.apiClientHelper.getForPromise(url);
	}

	reloadDashboardData(user) {
		this.loadDashboardData(user);
	}

	clearDashboardSaveData() {
		this.setStoredAgentDashboardData(null);
		this.setCoordinatorState(null);
		this.setMonitorGrafanaUrl(null);
		this.setAgentHasSurvey(false);
	}

	closeObservables() {
		this.dashboardData = new ReplaySubject(1);
		this.mobileCompulsoryModal = new ReplaySubject<string>(1);
	}

	setMobileCompulsoryModal(isModal) {
		this.mobileCompulsoryModal.next(isModal);
	}

	retryGetRmLoanDetails() {
		if (!this.retryRmDetailsCall) {
			this.getRelationshipManagerLoanDetails();
		} else {
			this.retryRmDetailsCall = true;
		}
	}

	getRelationshipManagerLoanDetails() {
		this.businessExpansionService
			.getRmLoanDetailsService()
			.then((data) => {
				if (data?.responseBody?.loanProducts) {
					if (data?.responseBody?.loanProducts?.length > 0) {
						sessionStorage.setItem('rm-business-expansion-details', 'true');
					}
				}
			})
			.catch((error) => {
				const message = StringHelper.getErrorMessageHttpErrorResponse(error);
			});
	}
}

// Generated by https://quicktype.io

export interface GraphData {
	withdrawals: Deposits;
	deposits: Deposits;
}

export interface Deposits {
	totalCount: number;
	totalValue: number;
	trend: Trend[];
}

export interface Trend {
	date: string;
	transactionsCount: number;
	transactionsValue: number;
}

export interface SingleConsent {
	consentRequestId: string;
	consentRequestTitle: string;
	consentRequestBody: string;
	consentLastUpdated: Date;
	consentStatus: ConsentStatus;
	isRevokable: boolean;
};

const CONSENT_STATUS = {
	PENDING: "PENDING",
	APPROVED: 'APPROVED',
	EXPIRED: 'EXPIRED',
	REJECTED: 'REJECTED',
	REVOKED: 'REVOKED',
} as const;

export type ConsentStatus = (typeof CONSENT_STATUS)[keyof typeof CONSENT_STATUS];

export interface GetConsentResponse {
	content: Array<SingleConsent>;
	numberOfElements: number;
}